// Zones — the named areas of a venue's floor (F-ORG-03; spec/13 §3.4, spec/04 §3).
//
//   GET   /v1/organizations/{o}/venues/{v}/zones             the venue's zones   floor:read
//   POST  /v1/organizations/{o}/venues/{v}/zones             create one          floor:arrange
//   PATCH /v1/organizations/{o}/venues/{v}/zones/{zoneId}    edit one            floor:arrange
//
// Main floor, Bootcamp, VIP room. A zone is a name, a colour token and the floor it is on; where it
// is — its rectangle — is on the floor plan (`routes/floor-plan.ts`), drawn in arrange mode. Zones
// are defined on the floor plan (spec/13 §12), so creating and editing one needs `floor:arrange`.
//
// A zone is used by pricing, reservations and reports, and is a scope in the settings chain
// (spec/03 §9). At most 30 per floor (spec/13 §3.4): the 31st is refused here and on the plan.
//
// `floor` is fixed at creation. Moving a zone to another level would leave its rectangle on the old
// level's plan; a zone that belongs elsewhere is a new zone.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, VenueIdSchema, ZoneIdSchema } from "../ids/schema.js";
import { ColorTokenSchema } from "../primitives/color-token.js";
import { FloorNumberSchema, MAX_ZONES_PER_FLOOR } from "../primitives/floor.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { FLOOR_ARRANGE, FLOOR_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
const nameSchema = z.string().trim().min(1).max(60);
export const ZoneSchema = z
    .object({
    id: ZoneIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    name: nameSchema,
    colorToken: ColorTokenSchema,
    floor: FloorNumberSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Zone', { description: 'A named area of the floor (spec/04 §3). Its rectangle is on the floor plan.' });
export const ZoneListSchema = paginated(ZoneSchema, 'ZoneList');
export const CreateZoneSchema = z
    .object({
    name: nameSchema,
    colorToken: ColorTokenSchema,
    /** Defaults to the ground floor. */
    floor: FloorNumberSchema.optional(),
})
    .strict()
    .openapi('CreateZone');
export const UpdateZoneSchema = z
    .object({ name: nameSchema, colorToken: ColorTokenSchema })
    .partial()
    // No `floor`: see the header. `.strict()` makes sending it a 400 rather than a silent no-op.
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one field to change.' })
    .openapi('UpdateZone');
const ZoneParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, zoneId: ZoneIdSchema })
    .openapi('ZoneParams');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const venueNotFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
export const listZonesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/zones',
    operationId: 'listZones',
    tags: ['floor'],
    summary: "List a venue's zones",
    description: 'Every floor, oldest first. Requires `floor:read`.',
    [PERMISSION_EXTENSION]: FLOOR_READ,
    request: { params: VenueScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: { description: 'A page of zones.', content: { [JSON_CONTENT_TYPE]: { schema: ZoneListSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createZoneRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/zones',
    operationId: 'createZone',
    tags: ['floor'],
    summary: 'Create a zone',
    description: `At most ${MAX_ZONES_PER_FLOOR} per floor. Requires \`floor:arrange\`. Audited.`,
    [PERMISSION_EXTENSION]: FLOOR_ARRANGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateZoneSchema } }, required: true },
    },
    responses: {
        201: { description: 'The zone that was created.', content: { [JSON_CONTENT_TYPE]: { schema: ZoneSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        409: problemResponse(`Another zone has that name, or the floor already has ${MAX_ZONES_PER_FLOOR} zones.`, 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateZoneRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/zones/{zoneId}',
    operationId: 'updateZone',
    tags: ['floor'],
    summary: 'Rename or recolour a zone',
    description: 'Requires `floor:arrange`. Audited with the before and after.',
    [PERMISSION_EXTENSION]: FLOOR_ARRANGE,
    request: {
        params: ZoneParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateZoneSchema } }, required: true },
    },
    responses: {
        200: { description: 'The zone as it is now.', content: { [JSON_CONTENT_TYPE]: { schema: ZoneSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such zone at this venue.', 'not-found'),
        409: problemResponse('Another zone at this venue already has that name.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=zones.js.map